<template>
  <div class="space-y-4">
    <div class="flex flex-col gap-2">
      <PriceRangeListItem
        v-for="(_, i) in formValues.priceRanges"
        :key="i"
        :price-range-index="i"
        :extra-type="initialExtraDto.type"
        :has-quantity-options="hasQuantityOptions"
        :errors="itemsWithErrors.items.get(i) ?? (itemsWithErrors.generalError ? [] : undefined)"
        :general-error-message="itemsWithErrors.generalItemErrors.get(i)"
        @close-modal="sortPriceRanges(i)"
        @delete="deletePriceRange(i)"
      />
    </div>
    <p v-if="itemsWithErrors.generalError" class="text-error">
      {{ itemsWithErrors.generalError }}
    </p>
    <AddItemButton :label="t('addPriceRange')" @click="addPriceRange" />
    <div v-if="hasChanged" class="flex justify-end gap-2">
      <CVButton variant="default" :is-loading="isPending" @click="handleSave">{{
        t('save')
      }}</CVButton>
      <CVButton variant="secondary" :disabled="isPending" @click="handleCancel">{{
        t('cancel')
      }}</CVButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFieldArray, useForm } from 'vee-validate';
import PriceRangeListItem from '@/views/yield-management/edit-extras/PriceRangeListItem.vue';
import { computed, ref, watch, watchEffect } from 'vue';
import type { EditExtraDto } from '@/views/yield-management/edit-extras/EditExtrasView.vue';
import AddItemButton from '@/views/yield-management/ui/AddItemButton.vue';
import { useI18n } from 'vue-i18n';
import type { ArrayType } from '@/utils/array-type.ts';
import { cloneDeep, maxBy } from 'lodash';
import { useFormHasChanged } from '@/hooks/use-form-has-changed.ts';
import { extraSchema } from '@/validation/extra.schema.ts';
import { Alert } from '@/services/alert.service';
import {
  VehicleExtraOptionPricingType,
  VehicleExtraType,
  type EditAutoAssignExtraOptionDto,
} from '@carvia/ros-client-types';
import { useEditAutoAssignVehicleExtra } from '@/queries/use-vehicle-extras.ts';

const props = defineProps<{
  initialExtraDto: EditExtraDto;
  hasQuantityOptions?: boolean;
}>();

const emit = defineEmits<{
  (e: 'change', value: boolean): void;
}>();

const { t } = useI18n();

const initialValues = ref<EditExtraDto>(cloneDeep(props.initialExtraDto));
if (props.hasQuantityOptions) {
  for (let i = 0; i < initialValues.value.priceRanges.length; i++) {
    const priceRange = initialValues.value.priceRanges[i];
    priceRange.maxQuantity = parseInt(
      maxBy(priceRange.options, (opt) => parseInt(opt.value))?.value ?? '0',
    );
  }
}

const {
  values: formValues,
  setFieldValue,
  resetForm,
  handleSubmit,
  errors,
  validate,
} = useForm<EditExtraDto>({
  initialValues: initialValues.value as EditExtraDto,
  keepValuesOnUnmount: true,
  validationSchema: extraSchema(props.hasQuantityOptions ?? false),
});

const { push, move, remove } = useFieldArray('priceRanges');

const itemsWithErrors = computed(() => {
  const items = new Map<number, number[]>();
  const generalItemErrors = new Map<number, string>();
  let generalError: string | undefined = undefined;
  Object.keys(errors.value).forEach((key) => {
    const pathParts = key.split('.');
    const priceRangeIndex = parseInt(pathParts[0].match(/\[(\d+)\]/)?.[1] ?? '-1');
    const optionIndex = pathParts[1] ? parseInt(pathParts[1].match(/\[(\d+)\]/)?.[1] ?? '0') : null;
    if (priceRangeIndex === -1) {
      generalError = errors.value[key as any] ?? t('notValid');
      return;
    }
    if (optionIndex === null) {
      const optionError = errors.value[key as any] ?? t('notValid');
      generalItemErrors.set(priceRangeIndex, optionError);
      return;
    }
    if (!items.has(priceRangeIndex)) {
      items.set(priceRangeIndex, []);
    }
    if (optionIndex !== null) {
      items.get(priceRangeIndex)?.push(optionIndex);
    }
  });
  return { generalError, generalItemErrors, items };
});

watch(
  formValues,
  () => {
    formValues.priceRanges.forEach((range, i) => {
      const diff = props.initialExtraDto.type === VehicleExtraType.EXTRA_KM ? 0.01 : 1;
      const toPrice = formValues.priceRanges[i + 1]
        ? formValues.priceRanges[i + 1].fromPrice - diff
        : null;
      if (range.toPrice !== toPrice) {
        // TODO Remove as any when fixed https://github.com/logaretm/vee-validate/issues/5074
        setFieldValue(`priceRanges[${i}].toPrice` as any, toPrice);
      }
    });
    validate();
  },
  { deep: true, immediate: true },
);

const addPriceRange = () => {
  const lastFromPrice = formValues.priceRanges[formValues.priceRanges.length - 1]?.fromPrice;
  const newPriceRange: ArrayType<EditExtraDto['priceRanges']> = {
    fromPrice: lastFromPrice ? lastFromPrice + 1 : 0,
    toPrice: null,
    options: [],
  };
  push(newPriceRange);
};

const sortPriceRanges = (index: number) => {
  const updatedRange = formValues.priceRanges[index];
  for (const [i, range] of formValues.priceRanges.entries()) {
    if (updatedRange.fromPrice <= range.fromPrice && i !== index) {
      if (index > i) {
        move(index, i);
      } else {
        move(index, i - 1);
      }
      return;
    }
  }
  move(index, formValues.priceRanges.length - 1);
};

const deletePriceRange = async (index: number) => {
  const { isDenied } = await Alert.fire({
    text: t('reallyDeletePriceRange'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (!isDenied) {
    return;
  }
  remove(index);
};

const hasChanged = useFormHasChanged(initialValues, formValues);
watch(hasChanged, () => {
  emit('change', hasChanged.value);
});

const handleCancel = () => {
  resetForm({ values: initialValues.value });
};

const { mutateAsync: save, isPending } = useEditAutoAssignVehicleExtra();

const handleSave = handleSubmit(async (values) => {
  const { isConfirmed } = await Alert.fire({
    text: t('reallySaveChanges'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('saveChanges'),
    denyButtonText: t('cancel'),
  });
  if (!isConfirmed) {
    return;
  }

  if (props.hasQuantityOptions) {
    values.priceRanges.forEach((priceRange, index) => {
      let optionWithValue1 = getOptionByValue(priceRange, 1);
      let options: EditAutoAssignExtraOptionDto[] = [];
      options.push({
        id: getOptionByValue(priceRange, 0)?.id,
        value: '0',
        price: null,
        pricePerDay: [{ fromDay: 1, pricePerDay: 0 }],
        pricingType: VehicleExtraOptionPricingType.FIXED_PER_DAY,
      });
      if (!optionWithValue1) {
        options.push({
          value: '1',
          price: null,
          pricePerDay: [],
          pricingType: VehicleExtraOptionPricingType.FIXED_PER_DAY,
        });
      } else {
        options.push(optionWithValue1);
      }
      optionWithValue1 = getOptionByValue(priceRange, 1)!;
      const pricePerDay = optionWithValue1.pricePerDay;
      for (let i = 2; i <= priceRange.maxQuantity!; i++) {
        const multipliedPricePerDay =
          pricePerDay?.map((p) => ({
            ...p,
            pricePerDay: p.pricePerDay * i,
          })) ?? null;
        options.push({
          id: getOptionByValue(priceRange, i)?.id,
          value: i.toString(),
          price: optionWithValue1.price ? optionWithValue1.price * i : null,
          pricePerDay: multipliedPricePerDay,
          pricingType: optionWithValue1.pricingType,
        });
      }
      priceRange.options = options;
    });
  }

  values.priceRanges.forEach((priceRange) => {
    priceRange.options.forEach((option) => {
      if (option.pricingType === VehicleExtraOptionPricingType.FIXED) {
        option.pricePerDay = null;
      } else {
        option.price = null;
      }
    });
  });
  await save({ dto: values });
  initialValues.value = cloneDeep(formValues);
});

function getOptionByValue(priceRange: ArrayType<EditExtraDto['priceRanges']>, value: number) {
  return priceRange.options.find((opt) => parseInt(opt.value) === value);
}
</script>

<i18n lang="json">
{
  "en": {
    "addPriceRange": "Add price range",
    "reallySaveChanges": "Do you really want to save? This will change the extras for all bookings from now on.",
    "reallyDeletePriceRange": "Do you really want to delete this price range?",
    "saveChanges": "Save changes",
    "notValid": "Your inputs are invalid"
  },
  "de": {
    "addPriceRange": "Preisspanne hinzufügen",
    "reallySaveChanges": "Möchtest du wirklich speichern? Dies wird die Extras für alle Buchungen ab jetzt ändern.",
    "reallyDeletePriceRange": "Möchtest du diese Preisspanne wirklich löschen?",
    "saveChanges": "Änderungen speichern",
    "notValid": "Deine Eingaben sind ungültig"
  },
  "es": {
    "addPriceRange": "Añadir tramo de precio",
    "reallySaveChanges": "¿Seguro que quieres guardar? Cambiará los extras para todas las reservas a partir de ahora.",
    "reallyDeletePriceRange": "¿Seguro que quieres eliminar este tramo de precio?",
    "saveChanges": "Guardar cambios",
    "notValid": "Los datos introducidos no son válidos"
  }
}
</i18n>
